import { describeApiError } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { useState } from 'react';
import { useSession } from '../api';
import {
  erasureButtonLabel,
  erasureDisclosure,
  erasureFailure,
  erasureFailureMessage,
  markErased,
  type ErasureCard,
} from '../erasure';
import { useEraseAccount } from '../queries';
import type { PortalTheme } from '../theme';

/**
 * Deleting your own Brush account (ADR 0010 Decision 7, ADR 0012 step 28), at the foot of the cards
 * screen — the screen about what this sign-in is connected to.
 *
 * Collapsed to a quiet link until asked for, then the three things the decision says a player is told
 * before confirming (`erasureDisclosure`), then one control that asks them to sign in again and deletes
 * with that sign-in. The wording and every failure's sentence are `erasure.ts`'s, tested there.
 *
 * <b>Never signal orange.</b> In this app orange means "this is you" and is never a control; a
 * destructive control wears the same bordered style as Disconnect beside it.
 */
export function EraseAccount({ cards, theme }: { cards: readonly ErasureCard[]; theme: PortalTheme }) {
  const [open, setOpen] = useState(false);
  const erase = useEraseAccount();
  const { endAfterErasure } = useSession();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          erase.reset();
          setOpen(true);
        }}
        className="mt-4 w-full text-center text-[11px] underline"
        style={{ color: theme.textMuted }}
      >
        Delete my Brush account
      </button>
    );
  }

  const { deleted, kept, signIn } = erasureDisclosure(cards);
  const failure = erase.error ? erasureFailure(erase.error) : null;
  const message = failure ? erasureFailureMessage(failure, describeApiError(erase.error)) : null;

  const list = (items: string[]) => (
    <ul className="mt-1 list-disc space-y-1 pl-4 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );

  return (
    <section className="mt-5 rounded-lg p-4" style={{ background: theme.panelLight, border: `1px solid ${theme.border}` }}>
      <h2 className="text-sm font-bold">Delete your Brush account</h2>

      <h3 className="mt-3 text-[11px] font-bold uppercase tracking-wider">What is deleted</h3>
      {list(deleted)}

      <h3 className="mt-3 text-[11px] font-bold uppercase tracking-wider">What each room keeps</h3>
      {list(kept)}

      <h3 className="mt-3 text-[11px] font-bold uppercase tracking-wider">Your sign-in</h3>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
        {signIn}
      </p>

      <p className="mt-3 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
        To confirm it is you, you will be asked to sign in again. The account is deleted straight after, and
        this cannot be undone.
      </p>

      {failure?.kind === 'nothing_to_erase' ? (
        <p role="status" className="mt-3 text-xs" style={{ color: theme.text }}>
          {message}
        </p>
      ) : (
        <ErrorNotice message={message} className="mt-3" />
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={erase.isPending}
          onClick={() =>
            erase.mutate(undefined, {
              onSuccess: () => {
                try {
                  markErased(window.sessionStorage);
                } catch {
                  // Blocked storage: the landing page simply does not repeat the news.
                }
                endAfterErasure();
              },
            })
          }
          className="rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          style={{ border: `1px solid ${theme.border}`, color: theme.text }}
        >
          {/* Two pending phases: waiting for the sign-in, then deleting — see erasurePhase. */}
          {erasureButtonLabel(erase.phase)}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={erase.isPending}
          className="rounded-lg px-3 py-1.5 text-xs disabled:opacity-50"
          style={{ color: theme.textMuted }}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}
